/**
 * Main Entry Point for Poker Reborn
 *
 * Bootstraps the PixiJS application and screen manager.
 */

import { Application } from 'pixi.js';
import { ScreenManager } from './screens/ScreenManager';
import { layoutService } from './services/LayoutService';
import { networkService } from './services/NetworkService';
import { accountService } from './services/AccountService';
import { navigateToScreen, setAuthenticated, clearAuthentication, setAccountIdentity, appState } from './state/AppState';
import { lobbyController } from './controllers/LobbyController';
import { tableController } from './controllers/TableController';
import { setupLogging, log } from './utils/log';
import { initializeEffectsSystem } from './effects/mapping/EffectsBridge';
import { preloadChipTextures } from './components/chips/ChipColors';
import { preloadCardTextures } from './services/AssetService';
import { showResolutionDisplay } from './utils/ResolutionDisplay';
import { IMAGES } from './config/assets';
import { Assets } from 'pixi.js';
import { avatarService } from './services/AvatarService';
import { getAvatarPath } from './config/avatars';
import { odooService } from './services/OdooService';
import { authService } from './services/AuthService';
import { Capacitor } from '@capacitor/core';

// =============================================================================
// URL PARAMETERS
// Supports: ?user=player2&pass=test123&watch=<croupier_glid>
// =============================================================================

const urlParams = new URLSearchParams(window.location.search);

// Authentication params - check URL first, then unified localStorage
const urlUsername = urlParams.get('user');
const urlPassword = urlParams.get('pass');

// If URL credentials provided, seed into localStorage and strip from URL
if (urlUsername && urlPassword) {
  authService.storeCredentials(urlUsername, urlPassword);

  urlParams.delete('user');
  urlParams.delete('pass');
  const cleanUrl = urlParams.toString()
    ? `${window.location.pathname}?${urlParams.toString()}`
    : window.location.pathname;
  window.history.replaceState({}, '', cleanUrl);
}

const storedCredentials = authService.getStoredCredentials();
const username = storedCredentials?.username ?? null;
const password = storedCredentials?.password ?? null;

// Auto-watch: croupier GLID to spectate on connect
const watchTableId = urlParams.get('watch');

// =============================================================================
// BOOTSTRAP
// =============================================================================

async function bootstrap(): Promise<void> {
  // Initialize logging first
  await setupLogging();

  log.app.info`Poker Reborn - Starting...`;

  // Get container element
  const container = document.getElementById('app');
  if (!container) {
    throw new Error('Container element #app not found');
  }

  // Create PixiJS application
  const app = new Application();

  // Load fonts + init WebGL in parallel (both independent, both slow)
  await Promise.all([
    document.fonts.load('400 16px Roboto'),
    document.fonts.load('500 16px Roboto'),
    document.fonts.load('700 16px Roboto'),
    document.fonts.load('700 16px Arvo'),
    document.fonts.load('700 16px Kalam'),
    app.init({
      background: 0x06060e,
      resizeTo: container,
      antialias: true,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
    }),
  ]);
  log.app.debug`Fonts + WebGL ready`;

  // Add canvas to DOM (hides the HTML pre-loader behind it)
  container.appendChild(app.canvas);

  // Disable right-click context menu on the game canvas
  app.canvas.addEventListener('contextmenu', (e) => { e.preventDefault(); });

  // Initialize layout service
  layoutService.init(container);

  // Initialize social login for native platforms (non-blocking)
  authService.initializeSocialLogin().catch((error) => {
    log.auth.warning`Social login init failed (non-fatal): ${error}`;
  });

  // Create screen manager (subscribes to appState.currentScreen)
  const screenManager = new ScreenManager(app);

  // Get loading screen for progress reporting
  const loadingScreen = screenManager.getLoadingScreen();

  // Remove HTML pre-loader now that PixiJS loading screen is rendering
  document.getElementById('preloader')?.remove();

  // Hide native splash screen (Capacitor) — PixiJS is now rendering
  if (Capacitor.isNativePlatform()) {
    import('@capacitor/splash-screen').then(({ SplashScreen }) => {
      SplashScreen.hide();
    }).catch(() => {
      // Plugin not installed or not available — ignore
    });
  }

  // Drive progress bar animation (smooth lerp + glow pulse)
  app.ticker.add((ticker) => {
    loadingScreen?.update(ticker.deltaTime);
  });

  // Load game assets with progress reporting
  loadingScreen?.setProgress(10, 'Loading effects...');
  await initializeEffectsSystem();
  log.effects.info`Effects system ready`;

  loadingScreen?.setProgress(30, 'Loading chips...');
  await preloadChipTextures();
  log.app.debug`Chip textures loaded`;

  loadingScreen?.setProgress(50, 'Loading cards...');
  await preloadCardTextures();
  log.app.debug`Card textures loaded`;

  loadingScreen?.setProgress(70, 'Loading table...');
  await Assets.load(IMAGES.dealerButton);
  log.app.debug`Dealer button texture loaded`;

  loadingScreen?.setProgress(85, 'Ready to connect...');
  log.app.debug`Screen manager initialized`;

  // Listen for connection diagnostics (detailed error info)
  networkService.on('connectionDiagnostic', (diagnostic) => {
    log.network.debug`Connection diagnostic: ${diagnostic.type} - ${diagnostic.message}`;
    loadingScreen?.showDiagnostic(diagnostic);
  });

  // Update browser tab title with player name
  appState.playerName.subscribe((name) => {
    document.title = name && name !== 'Guest' ? `PR - ${name}` : 'PR';
  });

  // Setup network event handlers
  setupNetworkHandlers();

  // Connect to server with retry support
  async function attemptConnection(connectUsername: string, connectPassword: string): Promise<void> {
    try {
      loadingScreen?.hideError();
      loadingScreen?.setProgress(90, 'Connecting...');

      log.network.info`Connecting as ${connectUsername}...`;
      await networkService.connect(connectUsername, connectPassword);
      const glid = networkService.getMyGlid() ?? connectUsername;
      log.network.info`Connected! GLID: ${glid}`;

      // Update app state with player profile
      setAuthenticated(
        glid,
        { id: glid, name: connectUsername },
        10000
      );

      // Load hero's avatar selection from localStorage and preload texture
      avatarService.loadFromStorage();
      const heroAvatarId = avatarService.getAvatarId();
      if (heroAvatarId !== null) {
        await Assets.load(getAvatarPath(heroAvatarId));
      }

      // Obtain fresh Odoo session token (non-blocking — app works without it)
      await odooService.authenticate(connectUsername, connectPassword);

      // Check for auto-watch mode (spectator via URL param)
      if (watchTableId) {
        log.app.info`Auto-watching table: ${watchTableId}`;
        await tableController.watchTableById(watchTableId);
        log.table.info`Watching table!`;
      } else {
        // Check for session recovery (active table lock)
        log.app.debug`Checking for active session...`;
        const activeLock = await accountService.checkSessionRecovery();

        if (activeLock) {
          // Player has an active table session - recover it
          log.table.info`Session recovery: rejoining table ${activeLock.croupier}`;
          await tableController.recoverTable(activeLock.croupier);
          log.table.info`Back at the table!`;
        } else {
          // Fetch onboarding config from Odoo (non-blocking)
          const onboarding = await odooService.getOnboarding();

          if (onboarding) {
            log.app.info`Onboarding: ${onboarding.onboarding.onboarding_type} (sessions: ${String(onboarding.onboarding.sessions)})`;
            if (import.meta.env.DEV) {

              console.log('[onboarding]', JSON.stringify(onboarding, null, 2));
            }

            // Store account identity (guest status + linked providers)
            setAccountIdentity(
              onboarding.onboarding.is_guest ?? false,
              onboarding.onboarding.linked_providers ?? [],
            );

            // TODO: Route to onboarding screen when features require it
          }

          // No active session - go to lobby
          log.lobby.debug`Loading games...`;
          await lobbyController.loadGames();

          // Navigate to lobby
          navigateToScreen('lobby');
          log.lobby.info`Welcome to the lobby!`;
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Connection failed';
      log.network.error`Connection failed: ${errorMessage}`;
      // Note: Error is already shown via connectionDiagnostic event listener
    }
  }

  // Check if we have credentials
  if (username && password) {
    // Silent validation for stored credentials (not URL params)
    let credentialsValid = true;
    if (!urlUsername && storedCredentials) {
      log.app.info`Validating stored credentials...`;
      loadingScreen?.setProgress(88, 'Validating session...');

      try {
        const validation = await odooService.validateCredentials(username, password);
        if (!validation.success) {
          log.auth.warning`Stored credentials invalid, clearing`;
          authService.clearCredentials();
          credentialsValid = false;
        } else {
          log.app.info`Stored credentials valid, connecting...`;
        }
      } catch (validationError) {
        // Network error or endpoint not available — proceed with stored credentials.
        // TypeError = network offline. Other errors = server returned non-200 (e.g. 404).
        // In both cases, skip validation and let WebSocket auth be the final arbiter.
        const errorMessage = validationError instanceof Error ? validationError.message : String(validationError);
        log.auth.warning`Validation skipped (${errorMessage}), proceeding with stored credentials`;
      }
    } else {
      log.app.info`Credentials found, connecting...`;
    }

    if (credentialsValid) {
      // Set up retry callback with stored credentials
      if (loadingScreen) {
        loadingScreen.onRetry = () => {
          log.network.info`Retrying connection...`;
          attemptConnection(username, password);
        };
      }

      // Initial connection attempt
      await attemptConnection(username, password);
    } else {
      // Credentials were invalid — fall through to show login screen
      log.app.info`Redirecting to login screen`;
      navigateToScreen('login');

      const loginScreen = screenManager.getLoginScreen();
      if (loginScreen) {
        loginScreen.onCredentialsReceived = (newUsername: string, newPassword: string) => {
          navigateToScreen('loading');
          if (loadingScreen) {
            loadingScreen.onRetry = () => { attemptConnection(newUsername, newPassword); };
          }
          attemptConnection(newUsername, newPassword);
        };
      }
    }
  } else if (import.meta.env.DEV && urlParams.get('dev') === 'buttons') {
    // Button showcase mode - skip login screen
    log.app.info`Button showcase mode, skipping login`;
  } else if (import.meta.env.DEV && urlParams.get('dev') === 'draws') {
    // Draw prediction showcase mode - skip login screen
    log.app.info`Draw prediction showcase mode, skipping login`;
  } else if (import.meta.env.DEV && urlParams.get('dev') === 'slots') {
    // Slot machine showcase mode - skip login screen
    log.app.info`Slot machine showcase mode, skipping login`;
  } else if (import.meta.env.DEV && urlParams.get('dev') === 'hilo') {
    // Hi-Lo showcase mode - skip login screen
    log.app.info`Hi-Lo showcase mode, skipping login`;
  } else if (import.meta.env.DEV && urlParams.get('dev') === 'wheel') {
    // Lucky wheel showcase mode - skip login screen
    log.app.info`Lucky wheel showcase mode, skipping login`;
  } else if (import.meta.env.DEV && urlParams.get('dev') === 'scratch') {
    // Scratch card showcase mode - skip login screen
    log.app.info`Scratch card showcase mode, skipping login`;
  } else if (import.meta.env.DEV && urlParams.get('dev') === 'jackpot') {
    // Jackpot slots showcase mode - skip login screen
    log.app.info`Jackpot slots showcase mode, skipping login`;
  } else {
    // No credentials - show login screen
    log.app.info`No credentials found, showing login screen`;
    navigateToScreen('login');

    // Get login screen and set up callback
    const loginScreen = screenManager.getLoginScreen();
    if (loginScreen) {
      loginScreen.onCredentialsReceived = (newUsername: string, newPassword: string) => {
        log.app.info`Credentials received from Google login`;

        // Navigate to loading screen
        navigateToScreen('loading');

        // Set up retry callback with new credentials
        if (loadingScreen) {
          loadingScreen.onRetry = () => {
            log.network.info`Retrying connection...`;
            attemptConnection(newUsername, newPassword);
          };
        }

        // Start connection
        attemptConnection(newUsername, newPassword);
      };
    }
  }

  // Store references for debugging and background tab recovery
  (window as unknown as { screenManager: ScreenManager }).screenManager = screenManager;
  (window as unknown as { pokerApp: Application }).pokerApp = app;

  log.app.info`Poker Reborn ready!`;

  // Show resolution display (debug)
  showResolutionDisplay();

  // Animation Emulator (dev only) - enable with ?anim-debug=true
  if (import.meta.env.DEV && urlParams.get('anim-debug') === 'true') {
    import('./dev/AnimationEmulator').then((m) => { m.initAnimationEmulator(); });
  }

  // Button Showcase (dev only) - enable with ?dev=buttons
  if (import.meta.env.DEV && urlParams.get('dev') === 'buttons') {
    import('./dev/ButtonShowcase').then((m) => { m.initButtonShowcase(app); });
  }

  // Draw Prediction Showcase (dev only) - enable with ?dev=draws
  if (import.meta.env.DEV && urlParams.get('dev') === 'draws') {
    import('./dev/DrawPredictionShowcase').then((m) => { m.initDrawPredictionShowcase(app); });
  }

  // Slot Machine Showcase (dev only) - enable with ?dev=slots
  if (import.meta.env.DEV && urlParams.get('dev') === 'slots') {
    import('./dev/SlotMachineShowcase').then((m) => { m.initSlotMachineShowcase(app); });
  }

  // Hi-Lo Showcase (dev only) - enable with ?dev=hilo
  if (import.meta.env.DEV && urlParams.get('dev') === 'hilo') {
    import('./dev/HiLoShowcase').then((m) => { m.initHiLoShowcase(app); });
  }

  // Lucky Wheel Showcase (dev only) - enable with ?dev=wheel
  if (import.meta.env.DEV && urlParams.get('dev') === 'wheel') {
    import('./dev/LuckyWheelShowcase').then((m) => { m.initLuckyWheelShowcase(app); });
  }

  // Scratch Card Showcase (dev only) - enable with ?dev=scratch
  if (import.meta.env.DEV && urlParams.get('dev') === 'scratch') {
    import('./dev/ScratchCardShowcase').then((m) => { m.initScratchCardShowcase(app); });
  }

  // Jackpot Slots Showcase (dev only) - enable with ?dev=jackpot
  if (import.meta.env.DEV && urlParams.get('dev') === 'jackpot') {
    import('./dev/JackpotSlotsShowcase').then((m) => { m.initJackpotSlotsShowcase(app); });
  }
}

/**
 * Setup network event handlers for debugging
 */
function setupNetworkHandlers(): void {
  networkService.on('message', ({ from, body }) => {
    // Ping messages are suppressed via LogTape category config
    if (body === '{"a":"ping"}') {
      log.ping.debug`Ping from ${from}`;
      return;
    }
    log.network.debug`Message from ${from}: ${body}`;
  });

  networkService.on('disconnected', ({ code, reason }) => {
    log.network.warning`Disconnected: ${code} ${reason}`;
    clearAuthentication();
  });

  networkService.on('reconnecting', ({ attempt, delay }) => {
    log.network.info`Reconnecting (attempt ${attempt}) in ${delay}ms...`;
  });

  networkService.on('reconnected', () => {
    log.network.info`Reconnected!`;
  });

  networkService.on('error', (error) => {
    log.network.error`Network error: ${error}`;
  });
}

// Start the app
bootstrap().catch((error) => {
  log.app.error`Failed to start Poker Reborn: ${error}`;
});
